import { Draft, castDraft } from 'immer';
import { HydrateAction, hydrateType } from '../HydrateAction';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { SessionState, SessionStateContent, SessionTokensState } from './SessionState';
import { SliceActions } from '../SliceActions';

const initialState: SessionState = {
  content: null
};

export interface SetContentPayload {
  readonly localChange: boolean;
  readonly content: SessionStateContent;
}

export interface SetTokensPayload {
  readonly localChange: boolean;
  readonly tokens: SessionTokensState | null;
}

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setContent(state: Draft<SessionState>, { payload }: PayloadAction<SetContentPayload>) {
      state.content = castDraft(payload.content);
    },
    setTokens(state: Draft<SessionState>, { payload }: PayloadAction<SetTokensPayload>) {
      const content = state.content;
      if (content === null) {
        throw new Error('State has not been initialized');
      }
      content.tokens = castDraft(payload.tokens);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateType, (state, action: HydrateAction) => {
      if (action.payload.session) {
        const hydratedContent = action.payload.session.content;
        if (
          hydratedContent !== null &&
          hydratedContent.tokens === null &&
          state.content?.guid === hydratedContent.guid
        ) {
          state.content = castDraft({ ...hydratedContent, tokens: state.content.tokens });
        } else {
          state.content = castDraft(hydratedContent);
        }
      }
    });
  }
});

export const { setContent, setTokens } = sessionSlice.actions;

export type ActionTypes = SliceActions<typeof sessionSlice.actions>;

export const setContentType = 'session/setContent';
export type SetContentAction = Extract<
  ActionTypes,
  PayloadAction<SetContentPayload, typeof setContentType>
>;

export const setTokensType = 'session/setTokens';
export type SetTokensAction = Extract<
  ActionTypes,
  PayloadAction<SetTokensPayload, typeof setTokensType>
>;

export default sessionSlice.reducer;
